import sortBy from "lodash/sortBy";
import * as Sentry from "@sentry/react";
import Transfer, { TransferResponse, TransferStatusTypes } from "../classes/transfer";
import TransferSource, { TransferSourceResponse } from "../classes/transferSource";
import TransferOptions, { TransferOptionsResponse } from "../classes/transferOptions";
import TransferDestination, {
  AccountType,
  DestinationType,
  TransferDestinationResponse,
} from "../classes/transferDestination";
import TransferEarnings, { TransferEarningsResponse } from "../classes/transferEarnings";
import CoreAPI from "./coreAPI";
import {
  AssetAllocationClass,
  ReallocationChoice,
  ReallocationSurveyData,
  AssetClassDataResponse,
} from "../classes/reallocation";
import TransferInstruction, { TransferInstructionResponse } from "../classes/transferInstruction";

export default function APITransfer(api: CoreAPI) {
  // We store everything into a methods object here so we don't lose scope
  const methods = {
    async getUserTransferInstructions({ userId }: { userId: string }) {
      const transferRes = await api.authCall<TransferInstructionResponse[]>({
        method: "GET",
        endpoint: `/api/userIdentity/${userId}/transfer/transferInstruction`,
      });
      return transferRes.map((transferInstructionRes: TransferInstructionResponse) =>
        TransferInstruction.fromAPI(transferInstructionRes)
      );
    },
    async getDestinationDetails(userId: string, transferId: string) {
      const transfer = await methods.getTransferById({ userId, transferId });
      return new TransferDestination(transfer.destinationAccount! || {});
    },

    async createOrUpdateDestination(userId: string, transferId: string, destination: TransferDestination) {
      const endpoint =
        destination.destinationAccountId === -1
          ? `/api/userIdentity/${userId}/transfer/${transferId}/destinationAccount`
          : `/api/userIdentity/${userId}/transfer/${transferId}/destinationAccount/${destination.destinationAccountId}/update`;
      const response = await api.authCall<TransferDestinationResponse>({
        method: "POST",
        endpoint,
        body: destination.toJSON(),
      });
      return TransferDestination.fromApi(response);
    },

    async updateSourceDetails(userId: string, transferId: string, source: TransferSource) {
      const endpoint =
        source.sourceAccountId === -1
          ? `/api/userIdentity/${userId}/transfer/${transferId}/sourceAccount`
          : `/api/userIdentity/${userId}/transfer/${transferId}/sourceAccount/${source.sourceAccountId}/update`;
      const response = await api.authCall<TransferSourceResponse>({
        method: "POST",
        endpoint,
        body: source.toJSON(),
      });
      return TransferSource.fromApi(response);
    },

    async getTransferOptions(userId: string) {
      let response: TransferOptionsResponse | null = null;
      try {
        response = await api.authCall<TransferOptionsResponse>({
          method: "GET",
          endpoint: `/api/userIdentity/${userId}/userPreference/transferOptions`,
        });
      } catch (e) {
        Sentry.captureMessage("No HENRY destination options is found");
      }
      return TransferOptions.fromAPI(response);
    },

    async getTransfersInProgress(params: { userId: string }) {
      const transfers = await api.authCall<TransferResponse[]>({
        method: "GET",
        endpoint: `/api/userIdentity/${params.userId}/transfer?transferStatus=${TransferStatusTypes.inProgress}`,
      });
      const mappedTransfers = transfers.map((transfer) => Transfer.fromAPI(transfer));
      return sortBy(mappedTransfers, (transfer) => {
        return transfer.sourceAccounts.length ? transfer.sourceAccounts[0].planSponsor.toLowerCase() : "";
      });
    },

    async getTransferById(params: { userId: string; transferId: string }) {
      const response = await api.authCall<TransferResponse>({
        method: "GET",
        endpoint: `/api/userIdentity/${params.userId}/transfer/${params.transferId}`,
      });
      return Transfer.fromAPI(response);
    },

    async initiateTransfer(params: { userId: string; transferId: string }) {
      return api.authCall({
        method: "POST",
        endpoint: `/api/userIdentity/${params.userId}/transfer/${params.transferId}/initiateTransfer`,
      });
    },

    async createNewTransfer(userId: string) {
      const { transferId } = await api.authCall<{
        transferId: string;
      }>({
        method: "POST",
        endpoint: `/api/userIdentity/${userId}/transfer`,
      });
      return methods.getTransferById({
        userId,
        transferId,
      });
    },

    async connectDestinationToTransfer(params: {
      userId: string;
      transferId: string;
      destination: TransferDestination;
    }) {
      const { userId, transferId, destination } = params;
      await methods.removeExistingDestinations(params);
      return api.authCall({
        method: "POST",
        endpoint: `/api/userIdentity/${userId}/transfer/${transferId}/addDestinationAccount`,
        body: destination,
      });
    },

    async removeDestinationAccount(params: {
      userId: string;
      transferId: string;
      destinationAccountId: number | string;
    }) {
      const { userId, transferId, destinationAccountId } = params;
      return api.authCall({
        method: "DELETE",
        endpoint: `/api/userIdentity/${userId}/transfer/${transferId}/destinationAccount/${destinationAccountId}`,
      });
    },

    async removeExistingDestinations(params: { userId: string; transferId: string }) {
      const { userId, transferId } = params;
      const transfer = await methods.getTransferById({ userId, transferId });
      if (!transfer.destinationAccount) return true;
      if (transfer.destinationAccount) {
        await methods.removeDestinationAccount({
          userId,
          transferId,
          destinationAccountId: transfer.destinationAccount.destinationAccountId,
        });
      }
      if (transfer.rothDestinationAccount) {
        await methods.removeDestinationAccount({
          userId,
          transferId,
          destinationAccountId: transfer.rothDestinationAccount.destinationAccountId,
        });
      }
    },

    async removeSourceAccount(params: { userId: string; transferId: string; sourceAccountId: number | string }) {
      const { userId, transferId, sourceAccountId } = params;
      return api.authCall({
        method: "DELETE",
        endpoint: `/api/userIdentity/${userId}/transfer/${transferId}/sourceAccount/${sourceAccountId}`,
      });
    },

    async removeTransfer(params: { userId: string; transferId: string }) {
      return api.authCall({
        method: "DELETE",
        endpoint: `/api/userIdentity/${params.userId}/transfer/${params.transferId}`,
      });
    },

    async uploadSourceStatement(params: { userId: string; transferId: string; fileName: string; file: File }) {
      const { userId, fileName, file, transferId } = params;
      const formData = new FormData();
      formData.append("file", file);
      return api.authCall({
        body: formData,
        method: "POST",
        endpoint: `/api/userIdentity/${userId}/transfer/${transferId}/sourceAccount/uploadStatementFile/${fileName}`,
        isMultiPartRequest: true,
      });
    },

    async uploadDestinationStatement(params: { userId: string; transferId: string; fileName: string; file: File }) {
      const { userId, fileName, file, transferId } = params;
      const formData = new FormData();
      formData.append("file", file);
      return api.authCall({
        body: formData,
        method: "POST",
        endpoint: `/api/userIdentity/${userId}/transfer/${transferId}/destinationAccount/uploadStatementFile/${fileName}`,
        isMultiPartRequest: true,
      });
    },

    async getSourceStatementUrl(params: { userId: string; statementFileName: string; transferId: string }) {
      const { userId, statementFileName, transferId } = params;
      const response = await api.authCall<string | undefined>({
        body: {
          statementFileName,
        },
        method: "POST",
        endpoint: `/api/userIdentity/${userId}/transfer/${transferId}/sourceAccount/viewStatement`,
      });
      return response || "";
    },

    async getDestinationStatementUrl(params: { userId: string; statementFileName: string; transferId: string }) {
      const { userId, statementFileName, transferId } = params;
      const response = await api.authCall<string | undefined>({
        body: {
          statementFileName,
        },
        method: "POST",
        endpoint: `/api/userIdentity/${userId}/transfer/${transferId}/destinationAccount/viewStatement`,
      });
      return response || "";
    },

    /**
     * Call delete statement only before the account is created. Do not call it when they
     * remove a statement during account update. Updating an account with the statement file name
     * not containing the file will automatically delete the file. [If this might be a problem to
     * differentiate on the front end lets discuss it further]
     */
    async deleteSourceStatement(params: { userId: string; transferId: string; statementFileName: string }) {
      const { userId, transferId, statementFileName } = params;
      await api.authCall({
        body: {
          statementFileName,
        },
        method: "DELETE",
        endpoint: `/api/userIdentity/${userId}/transfer/${transferId}/sourceAccount/deleteStatement`,
      });
      return true;
    },

    async deleteDestinationStatement(params: { userId: string; transferId: string; statementFileName: string }) {
      const { userId, transferId, statementFileName } = params;
      await api.authCall({
        body: {
          statementFileName,
        },
        method: "DELETE",
        endpoint: `/api/userIdentity/${userId}/transfer/${transferId}/destinationAccount/deleteStatement`,
      });
      return true;
    },

    async getDestinationAccountsByType(params: {
      userId: string;
      destinationType: DestinationType;
      destinationAccountType?: AccountType;
    }) {
      const { userId, destinationType, destinationAccountType } = params;
      let query = `destinationType=${destinationType}`;
      if (destinationAccountType) query += `&destinationAccountType=${destinationAccountType}`;
      const response = await api.authCall<TransferDestinationResponse[]>({
        method: "GET",
        endpoint: `/api/userIdentity/${userId}/transfer/destinationAccount?${query}`,
      });
      return response.map((transferDestinationResponse) => TransferDestination.fromApi(transferDestinationResponse));
    },

    async uploadAdhocDoc(params: { userId: string; transferInstructionId: number; file: File }) {
      const { userId, transferInstructionId, file } = params;

      const formData = new FormData();
      formData.append("file", file);
      return api.authCall({
        body: formData,
        method: "POST",
        endpoint: `/api/userIdentity/${userId}/transfer/transferInstruction/${transferInstructionId}/adhocDocs`,
      });
    },

    async deleteAdhocDoc(params: { userId: string; transferInstructionId: number; adhocDocName: string }) {
      const { userId, transferInstructionId, adhocDocName } = params;
      return api.authCall({
        method: "DELETE",
        endpoint: `/api/userIdentity/${userId}/transfer/transferInstruction/${transferInstructionId}/${adhocDocName}`,
      });
    },

    async getAdhocDoc(params: { userId: string; transferInstructionId: number; adhocDocName: string }) {
      const { userId, transferInstructionId, adhocDocName } = params;
      return api.authCall({
        method: "GET",
        endpoint: `/api/userIdentity/${userId}/transfer/transferInstruction/${transferInstructionId}/${adhocDocName}`,
      });
    },

    async getEarningsByTransferId(params: { transferId: string | number; userId: string }) {
      const { transferId, userId } = params;
      const response = await api.authCall<TransferEarningsResponse>({
        method: "GET",
        endpoint: `/api/userIdentity/${userId}/transfer/${transferId}/earnings`,
      });
      return TransferEarnings.fromAPI(response);
    },

    async createReallocation(params: {
      reallocationFormData: ReallocationSurveyData;
      destinationAccountId: number | string;
    }): Promise<any> {
      const { reallocationFormData, destinationAccountId } = params;
      return await api.authCall({
        method: "POST",
        endpoint: "/api/reallocation",
        body: {
          ...reallocationFormData,
          destinationAccountId,
        },
      });
    },

    async updateReallocationChoice(params: {
      choice: ReallocationChoice;
      reallocationDetails: null | AssetAllocationClass;
      transferId: number;
      destinationAccountId: number | string;
      userId: string;
      reallocationId: number | null;
    }) {
      const { choice, reallocationDetails, reallocationId, transferId, destinationAccountId, userId } = params;
      if (choice === ReallocationChoice.LEAVEASIS) {
        return await api.authCall({
          method: "PATCH",
          endpoint: `/api/userIdentity/${userId}/transfer/${transferId}/destinationAccount/${destinationAccountId}/skipReallocation`,
          body: {
            skipReallocation: true,
          },
        });
      }
      if (reallocationId && reallocationDetails) {
        await api.authCall({
          method: "PATCH",
          endpoint: `/api/reallocation/${reallocationId}/equityAllocation`,
          body: {
            equityAllocation: parseInt(reallocationDetails.split("-")[0]),
          },
        });
      }

      await api.authCall({
        method: "PATCH",
        endpoint: `/api/userIdentity/${userId}/transfer/${transferId}/destinationAccount/${destinationAccountId}/skipReallocation`,
        body: {
          skipReallocation: false,
        },
      });
    },

    async deleteReallocationData(reallocationId: number) {
      api.authCall({
        method: "DELETE",
        endpoint: `/api/reallocation/${reallocationId}`,
      });
    },

    async getReallocationCalculation({
      userId,
      reallocationId,
    }: {
      userId: string | number;
      reallocationId: number;
    }): Promise<AssetClassDataResponse> {
      const response = await api.authCall({
        method: "GET",
        endpoint: `/api/userIdentity/${userId}/transfer/destinationAccount/reallocation/${reallocationId}/calculateEquityAllocation`,
      });
      return response as AssetClassDataResponse;
    },
    async getDestinationByTransferId({ userId, transferId }: { userId: string; transferId: string | number }) {
      try {
        const res = await api.authCall<TransferDestinationResponse>({
          method: "GET",
          endpoint: `/api/userIdentity/${userId}/transfer/${transferId}/destinationAccount`,
        });
        return TransferDestination.fromApi(res);
      } catch (e) {
        return undefined;
      }
    },
  };

  return methods;
}
